<template>
  <div class="container-xxl py-25">
    <MetaTags
      :title="`${ summary.client.name} ${summary.client.surname} - Видео-резюме`"
      :description="`Посмотрите видео-резюме ${summary.client.name} ${summary.client.surname} и узнайте больше о его навыках и опыте. Свяжитесь с нами для сотрудничества!`"
    />
    <AppBreadcrumbs
      :links="[
        {
          text: `Кандидаты ${summary.city.titleIn ?? 'в ' + summary.city.title}`,
          alias: `/${summary.city.alias}/summaries`
        },
        {
          text: `${summary.client.name} ${summary.client.surname}`,
        }
      ]"
    />
    <h1 class="heading-header-2">
      {{ summary.client.name }} {{ summary.client.surname }}
    </h1>
    <div class="row">
      <div class="col-12 col-md-9">
        <div class="row">
          <div class="col-12 col-md-5 col-lg-4">
            <VideoPlayer
              id="videoPlayer"
              class="rounded-20 overflow-hidden"
              :answers="getAnswers"
              :only-step="true"
            />
          </div>
          <div class="col-12 col-md-7 col-lg-8 mt-10 mt-md-0">
            <WindowItem>
              <div class="d-flex gap-24 justify-content-center">
                <AppButton
                  class="btn-book"
                  :class="summary.favorite === null ? 'border booked' : null"
                  title="Добавить в избранное"
                  :active="summary.favorite === null"
                  variant
                  is-icon
                  @click="favoriteControl"
                >
                  <svg class="h-100 w-100">
                    <use xlink:href="/public/icons/main.svg#heart-fill" />
                  </svg>
                </AppButton>

                <AppButton
                  class="rounded-10"
                  @click="openInviteModal"
                >
                  Пригласить
                </AppButton>
              </div>
              <h1 class="heading-section-3">
                {{ summary.client.name }} {{ summary.client.surname }}
              </h1>
              <div class="mb-20">
                <div class="fw-bold">
                  Контакты
                </div>
                <div class="mt-5">
                  <a
                    class="text-decoration-none text-primary"
                    :href="'tel:' + getPhone"
                  >
                    {{ getPhone }}
                  </a>
                </div>
                <div class="d-flex flex-wrap mt-10">
                  <a
                    v-if="getSocial.whatsapp !== undefined"
                    class="d-flex me-20 align-items-center text-decoration-none text-black"
                    title="Whatsapp"
                    :href="`https://wa.me/${getSocial.whatsapp}`"
                    target="_blank"
                  >
                    <svg
                      height="25"
                      width="25"
                      class="me-5"
                    >
                      <use xlink:href="/public/icons/whatsapp.svg#whatsapp" />
                    </svg>
                    <span>Whatsapp</span>
                  </a>
                  <a
                    v-if="getSocial.telegram !== undefined"
                    class="d-flex me-20 align-items-center text-decoration-none text-black"
                    title="Telegram"
                    :href="`https://t.me/+${getSocial.telegram}`"
                    target="_blank"
                  >
                    <svg
                      height="25"
                      width="25"
                      class="me-5"
                    >
                      <use xlink:href="/public/icons/telegram-blue.svg#telegram-blue" />
                    </svg>
                    <span>Telegram</span>
                  </a>
                  <a
                    v-if="getSocial.viber !== undefined"
                    class="d-flex align-items-center text-decoration-none text-black"
                    title="Должно быть установлено приложение Viber"
                    :href="`viber://chat?number=+${getSocial.viber}`"
                    target="_blank"
                  >
                    <svg
                      height="25"
                      width="25"
                      class="me-5"
                    >
                      <use xlink:href="/public/icons/viber.svg#viber" />
                    </svg>
                    <span>Viber</span>
                  </a>
                </div>
              </div>
              <div class="mb-20">
                <div class="fw-bold">
                  Возраст
                </div>
                {{ summary.age }}
              </div>
              <div class="mb-20">
                <div class="fw-bold">
                  Профессии
                </div>
                {{ summary.professions.map((val) => val.title).join(', ') }}
              </div>
              <div class="mb-20">
                <div class="fw-bold">
                  Опыт работы в профессии
                </div>
                {{ summary.experienceInProfession.title }}
              </div>
              <div class="mb-20">
                <div class="fw-bold">
                  Общий стаж, лет
                </div>
                {{ summary.experience }}
              </div>
              <div class="mb-20">
                <div class="fw-bold">
                  Желаемый график работы
                </div>
                {{ summary.schedules.map(val => val.name).join(', ') }}
              </div>
              <div class="d-flex gap-24 justify-content-center align-items-center">
                <span>
                  Дата изменения:
                  {{ new Date(summary.updated * 1000).toLocaleString(['ru-RU', 'en-US'], {day: '2-digit', month: '2-digit', year: 'numeric'}) }}
                </span>
              </div>
            </WindowItem>
          </div>
        </div>
      </div>
    </div>
    <LazyInviteModel />
  </div>
</template>

<script setup lang="ts">
import type { Profession } from "~/entities/profession";
import { useClient } from "~/stores/client";
import { useApiFavorite } from "~/composables/api/favorite";
import InviteModel from "~/components/modal/inviteModel.vue";
import { useModal } from "vue-final-modal";
import type { VideoData } from "~/entities/videoData";
import AuthorizationModal from "~/components/modal/AuthorizationModal.vue";
import {usePhoneNormalize} from "~/composables/utils/phoneNormalize";

const { $toast } = useNuxtApp();
const client = useClient();

let favorite = useApiFavorite(client.getClientId!);
const route = useRoute();
const useInviteModal = useModal({
  component: InviteModel,
  attrs: {
    onConfirm() {
      useInviteModal.close();
    }
  }
});

const useAuthorizationModal = useModal({
  component: AuthorizationModal,
  attrs: {
    onConfirm() {
      useAuthorizationModal.close();
    },
    onLogin() {
      favorite = useApiFavorite(client.getClientId);
      getSummary();
    }
  }
});

//-----TYPE-----\\
type Summary = {
  client: Client,
  city: City,
  professions: Profession[],
  questions: Object[],
  favorite: { id: string }|null,
  updated: number,
  created: number
};

//-----STATE-----\\
const summary = ref<Summary>();

//-----COMPUTED-----\\
const getAnswers = computed((): VideoData[] => {
  return summary.value!.questions.map((question: any) => ({
    question: question.question,
    src: question.answer?.src,
    isAssembled: question.answer?.isAssembled??false,
    duration: question.answer.duration,
  } as VideoData));
});
const getSocial = computed<Object[]>(() => {
  if (summary.value.social !== null && Object.keys(summary.value.social).length > 0) {
    return summary.value.social;
  }

  return [];
});
const getPhone = computed(() => {
  return usePhoneNormalize(summary.value?.client.phone);
});

//-----ASYNC-----\\
await getSummary();
async function getSummary() {
  const { data, error } = await mainFetch(`summaries/${route.params.summary_id}`);
  if (error.value !== null) {
    throw createError({
      // status: error.value?.data.error.code,
      status: error.value?.data.error.code,
      statusMessage: error.value?.data.error.clientMessage,
    });
  }

  //TODO: Проверка на 404
  summary.value = data.value.summary;
}

//-----METHODS-----\\
async function favoriteControl() {
  if (!client.isAuthenticated) {
    useAuthorizationModal.open();
    return;
  }

  if (summary.value.favorite === null) {
    const id: string|null = await favorite.add(route.params.summary_id as string, 0);
    if (id !== null) {
      summary.value.favorite = { id };
    }
    return;
  }

  const isAdded: boolean = await favorite.delete(summary.value.favorite.id);
  if (isAdded) {
    summary.value.favorite = null;
  }
}
function openInviteModal() {
  if (!client.isAuthenticated) {
    useAuthorizationModal.open();
    return;
  }

  if (client.getActiveCompany?.id !== undefined) {
    useInviteModal.options.attrs.client = client.id;
    useInviteModal.open();
  } else {
    $toast.error('Соискатель не может приглашать кандидатов');
    return;
  }
}
</script>

<style scoped lang="scss">
@media (max-width: 767px) {
  #videoPlayer {
    height: 90vh !important;
  }
}
</style>
